import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Clock, CheckCircle, Package, User, Phone, MapPin, Download, Share2, RefreshCw, Repeat } from 'lucide-react';
import { downloadInvoice, shareInvoiceViaWhatsApp } from '@/utils/invoiceGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTodayOrders = ({ adminToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingAll, setGeneratingAll] = useState(false);

  useEffect(() => {
    fetchTodayOrders();
  }, []);

  const fetchTodayOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders/today`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch today orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      fetchTodayOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleGenerateAll = async () => {
    setGeneratingAll(true);
    try {
      await axios.post(
        `${API}/admin/subscriptions/generate-all-today`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Generating subscription orders in background. Refreshing in 3s...');
      setTimeout(fetchTodayOrders, 3000);
    } catch (error) {
      toast.error('Failed to trigger generation');
    } finally {
      setGeneratingAll(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      pending_payment: 'bg-orange-100 text-orange-800 border-orange-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      preparing: 'bg-purple-100 text-purple-800 border-purple-300',
      ready: 'bg-cyan-100 text-cyan-800 border-cyan-300',
      out_for_delivery: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="text-center py-8">Loading today's orders...</div>;
  }

  if (!data) return null;

  const OrderCard = ({ order }) => (
    <Card className={`p-4 border-l-4 ${order.is_subscription_order ? 'border-l-emerald-500' : order.order_status === 'pending' || order.order_status === 'pending_payment' ? 'border-l-orange-500' : 'border-l-primary'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-bold text-accent">#{order.id.slice(0, 8).toUpperCase()}</span>
            <Badge className={getStatusColor(order.order_status)}>
              {order.order_status.replace('_', ' ')}
            </Badge>
            {order.is_subscription_order && (
              <Badge className="bg-emerald-100 text-emerald-800 gap-1 text-xs">
                <Repeat className="h-3 w-3" />Sub
              </Badge>
            )}
            {order.scheduled_date && (
              <Badge variant="outline" className="gap-1">
                <Clock className="h-3 w-3" />
                Scheduled
              </Badge>
            )}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{order.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <a href={`tel:${order.phone}`} className="text-primary hover:underline">{order.phone}</a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{order.delivery_address}</span>
            </div>
          </div>
          <div className="mt-3 p-2 bg-gray-50 rounded">
            <p className="text-xs text-muted-foreground mb-1">Items:</p>
            <ul className="text-sm space-y-1">
              {order.items?.map((item, idx) => (
                <li key={idx} className="flex justify-between">
                  <span>{item.product?.name || item.name} x{item.quantity}</span>
                  <span className="text-muted-foreground">₹{(item.product?.price || item.price) * item.quantity}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="text-right space-y-2">
          <p className="text-2xl font-bold text-primary">₹{order.total}</p>
          <Select
            value={order.order_status}
            onValueChange={(value) => handleStatusChange(order.id, value)}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="preparing">Preparing</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => {
                try {
                  downloadInvoice(order);
                  toast.success('Invoice downloaded!');
                } catch (error) {
                  toast.error('Failed to download invoice');
                }
              }}
            >
              <Download className="h-3 w-3" />
              Invoice
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => {
                try {
                  shareInvoiceViaWhatsApp(order);
                  toast.success('Opening WhatsApp...');
                } catch (error) {
                  toast.error('Failed to share');
                }
              }}
            >
              <Share2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Subscription Pending Banner */}
      {data.subscription_pending?.length > 0 && (
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Repeat className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-800">
                  {data.subscription_pending.length} Subscription{data.subscription_pending.length > 1 ? 's' : ''} Pending Order Generation
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  {data.subscription_pending.map(s => s.product?.name || 'Product').join(', ')}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 shrink-0"
              onClick={handleGenerateAll}
              disabled={generatingAll}
            >
              {generatingAll ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Repeat className="h-3.5 w-3.5" />}
              Generate All
            </Button>
          </div>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <p className="text-3xl font-bold text-accent">{data.total}</p>
          <p className="text-sm text-muted-foreground">Total Orders</p>
        </Card>
        <Card className="p-4 text-center bg-orange-50">
          <p className="text-3xl font-bold text-orange-600">{data.pending?.length || 0}</p>
          <p className="text-sm text-muted-foreground">Pending</p>
        </Card>
        <Card className="p-4 text-center bg-blue-50">
          <p className="text-3xl font-bold text-blue-600">{data.confirmed?.length || 0}</p>
          <p className="text-sm text-muted-foreground">Confirmed</p>
        </Card>
        <Card className="p-4 text-center bg-green-50">
          <p className="text-3xl font-bold text-green-600">{data.completed?.length || 0}</p>
          <p className="text-sm text-muted-foreground">Completed</p>
        </Card>
      </div>

      {/* Pending Orders - Priority */}
      {data.pending?.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-orange-600 mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pending Orders ({data.pending.length})
          </h3>
          <div className="space-y-3">
            {data.pending.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        </div>
      )}

      {/* Confirmed Orders */}
      {data.confirmed?.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-blue-600 mb-3 flex items-center gap-2">
            <Package className="h-5 w-5" />
            Confirmed Orders ({data.confirmed.length})
          </h3>
          <div className="space-y-3">
            {data.confirmed.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Orders */}
      {data.completed?.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-green-600 mb-3 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Completed Orders ({data.completed.length})
          </h3>
          <div className="space-y-3">
            {data.completed.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        </div>
      )}

      {data.total === 0 && (
        <Card className="p-8 text-center">
          <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium text-muted-foreground">No orders for today</h3>
          <p className="text-sm text-muted-foreground">Orders will appear here as they come in</p>
        </Card>
      )}
    </div>
  );
};

export default AdminTodayOrders;
